/**
 * BrandDetailContainer
 * --
 */
import React, { useState, useEffect } from 'react';
import BrandDetailPresenter from './BrandDetailPresenter';
import MessageAlert, { HOST_URL, STORE_URL } from 'utils';
import {
  ShopInformationApi,
  ProductApi,
  ShopInformationFileApi,
  AdminApi,
} from 'api';
import { APPLICATION_STATUS } from 'constants/ProductConstants';
import axios from 'axios';

/**----------------------------------------
 * [Component] 입점업체 상세: 컨테이너
 * ----------------------------------------
 */
const BrandDetailContainer = ({
  defaultShopId,
  onChangeShopInformation,
  permission,
  productAuditMode,
}) => {
  // console.log('permission: ', permission);

  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  /* ===== STATE ===== */
  const [loading, setLoading] = useState(false);
  const [shopId, setShopId] = useState(defaultShopId);
  const [shopInfomation, setShopInformation] = useState(null);
  const [shopInformationFiles, setShopInformationFiles] = useState({
    thumbnail: null,
    detail: null,
    license: null,
  });
  const [productList, setProductList] = useState([]);

  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */

  /**--------------------
   * 업체정보(shopInformation) 데이터 변경
   * --------------------
   */
  const handleChangeApplicationStatus = async (key, newStatus) => {
    setLoading(true);
    try {
      if (!newStatus) {
        MessageAlert.warning('값을 입력해주세요');
        setLoading(false);
        return;
      }

      // New data
      const newData = {
        shop_information_id: shopId,
        [key]: newStatus,
      };

      // API Call
      const { status, message, data } =
        await ShopInformationApi.updateShopInformation(newData);
      // 예외처리
      if (status !== 200) throw { message };
      // 상위 컴포넌트로 전달
      onChangeShopInformation(data);

      // NOTE: 업체 상태에 따른 관리자 권한 레벨 변경
      if (key === 'application_status') {
        // 업체 관리자 조회
        const { status: getAdminStatus, data: getAdminData } =
          await AdminApi.getAdmins({ shop_information_id: shopId });

        if (getAdminStatus !== 200) {
          MessageAlert.info(
            '관리자 권한 변경에 실패하였습니다!\n수동으로 변경이 필요합니다.'
          );
        }

        // 관리자 권한 레벨 변경
        const updateData = getAdminData.map((_item) => ({
          admin_id: _item?.admin_id,
          admin_level: newStatus === 'APPROVE' ? 2 : 3,
        }));

        updateData?.forEach(async (_item) => await AdminApi.updateAdmin(_item));
      }

      setTimeout(() => {
        // 메시지 처리
        if (key === 'application_status') {
          const name = shopInfomation.shop_information_name;
          const value = APPLICATION_STATUS[newStatus].title;
          const msg = `${name}의 심사상태가 [${value}]변경되었습니다`;

          MessageAlert.info(msg);
        } else if (key === 'is_brand') {
          const name = shopInfomation.shop_information_name;
          MessageAlert.info(`${name}의 브랜드 여부가 변경되었습니다`);
        } else {
          MessageAlert.info('피드백이 등록되었습니다');
        }
        // set state
        setShopInformation(data);
        setLoading(false);
      }, 1000);
    } catch (e) {
      setLoading(false);
      console.log(
        '[BrandDetailContainer][handleChangeApplicationStatus] Error: ',
        e
      );
      MessageAlert.error(e.message);
    }
  };

  /**--------------------
   * 상품(product) 데이터 변경
   * --------------------
   */
  const handleChangeProduct = async (product_id, key, newStatus) => {
    setLoading(true);
    try {
      if (!newStatus) {
        MessageAlert.warning('값을 입력해주세요');
        setLoading(false);
        return;
      }
      const fid = productList.findIndex((p) => p.product_id === product_id);
      const newData = {
        product_id,
        [key]: newStatus,
      };

      // API Call
      const { status, message } = await ProductApi.updateProduct(newData);
      // 예외처리
      if (status !== 200) throw { message };

      setTimeout(() => {
        MessageAlert.info('변경되었습니다.');
        setLoading(false);
        setProductList((prev) => [
          ...prev.slice(0, fid),
          {
            ...prev[fid],
            ...newData,
          },
          ...prev.slice(fid + 1),
        ]);
      }, 1000);
    } catch (e) {
      setLoading(false);
      console.log('[BrandDetailContainer][handleChangeProduct] Error: ', e);
      MessageAlert.error(e.message);
    }
  };

  /**
   * 파일 다운로드
   * @param {string} url
   */
  const handleFileDownload = async (key, name) => {
    try {
      const { data: responseData } = await axios.post(
        `${HOST_URL}/file/download`,
        {
          type: 'buffer',
          file_name: key,
        }
      );

      if (responseData.status !== 200) {
        MessageAlert.info('다운로드에 실패하였습니다.');
        console.error('다운로드 실패:', responseData.message);
        return;
      }

      const bufferDataArray = responseData.data.data;

      const uint8Array = new Uint8Array(bufferDataArray);
      const arrayBuffer = uint8Array.buffer;
      const contentType = 'image/png';

      const blob = new Blob([arrayBuffer], { type: contentType });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', name); // 다운로드될 파일 이름
      document.body.appendChild(link);

      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('[handleFileDownload] Error!! ', error);
      MessageAlert.error('다운로드에 실패하였습니다.');
    }
  };

  /**
   * 수정모달에서 수정한 데이터 업데이트
   * --
   * @param {object} data
   * @param {object} files
   */
  const handleRefetchData = (data, files) => {
    // 상위 컴포넌트로 전달
    onChangeShopInformation(data);
    setShopInformation(data);

    const shopFile = Object.entries(files).reduce(
      (acc, [key, value]) => {
        if (key === 'THUMBNAIL') {
          acc['thumbnail'] = value;
        } else if (key === 'STORE') {
          acc['detail'] = value;
        } else if (key === 'LICENSE') {
          acc['license'] = value;
        }

        return acc;
      },
      { thumbnail: null, detail: null, license: null }
    );

    setShopInformationFiles(shopFile);
  };

  /* ===== HOOKS ===== */
  /**--------------------
   * 입점업체 상세 조회
   * --------------------
   */
  useEffect(() => {
    const call = async () => {
      try {
        const { status, message, data } =
          await ShopInformationApi.getShopInformation(shopId);

        const {
          status: fileStatus,
          message: fileMessage,
          data: fileData,
        } = await ShopInformationFileApi.getShopInformationFiles({
          shop_information_id: shopId,
        });

        if (status !== 200) throw { message };

        if (fileStatus !== 200) throw { fileMessage };

        const shopFile = fileData.reduce(
          (acc, cur) => {
            const { type } = cur;

            if (type === 'THUMBNAIL') {
              acc['thumbnail'] = cur;
            } else if (type === 'STORE') {
              acc['detail'] = cur;
            } else if (type === 'LICENSE') {
              acc['license'] = cur;
            }

            return acc;
          },
          { thumbnail: null, detail: null, license: null }
        );

        setShopInformationFiles(shopFile);

        setShopInformation(data);
      } catch (e) {
        MessageAlert.error(e.message);
      }
    };

    call();
  }, []);

  /**--------------------
   * 입점업체 상품 조회
   * --------------------
   */
  useEffect(() => {
    const call = async () => {
      try {
        const { status, message, data } = await ProductApi.getProducts({
          shop_information_id: shopId,
        });
        if (status !== 200) throw { message };
        setProductList(data);
      } catch (e) {
        MessageAlert.error(e.message);
      }
    };

    shopInfomation && call();
  }, [shopInfomation]);
  /* ===== RENDER ===== */
  return (
    <BrandDetailPresenter
      {...{
        loading,
        shopId,
        shopInfomation,
        productList,
        shopInformationFiles,
        onChangeApplicationStatus: handleChangeApplicationStatus,
        onChangeProduct: handleChangeProduct,
        onFileDownload: handleFileDownload,
        onRefetchData: handleRefetchData,
      }}
    />
  );
};

/* Default props */
BrandDetailContainer.defaultProps = {
  defaultShopId: null,
  permission: 'ADMIN',
  productAuditMode: false,
  onChangeShopInformation: () => {},
};
export default BrandDetailContainer;
